import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type RailTooltipProps = {
  /** Lo que dice el globo: el mismo nombre que el `aria-label` del control. */
  label: string;
  /** Solo con el riel contraído: expandido el nombre ya está a la vista. */
  enabled: boolean;
  children: ReactNode;
  /** Clases del envoltorio (ej. el botón de contraer, que flota con `absolute`). */
  className?: string;
};

const SHOW_DELAY_MS = 80;

const isKeyboardFocus = (target: EventTarget): boolean => {
  try {
    return (target as HTMLElement).matches(':focus-visible');
  } catch {
    // Motores sin `:focus-visible` (jsdom viejo): mejor mostrarlo que perderlo.
    return true;
  }
};
const GAP = 10;

/**
 * Globo oscuro al costado del riel contraído, en lugar del `title` nativo (que tarda,
 * no respeta la tipografía y tapa lo que está abajo). Es solo visual: el nombre
 * accesible lo sigue dando el `aria-label` del control, por eso el globo va
 * `aria-hidden` y no se lee dos veces. Se portalea a `body` para no quedar recortado
 * por el scroll del riel.
 */
export const RailTooltip: FunctionComponent<RailTooltipProps> = ({ label, enabled, children, className }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  // Después de un clic el globo no vuelve hasta que el mouse sale: si no, quedaba
  // encima del panel o la pantalla que ese clic acaba de abrir.
  const suppressed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  // Si el riel se mueve (scroll de la lista o de la página) el globo quedaría flotando
  // lejos de su ícono: se cierra.
  useEffect(() => {
    if (!coords) return;
    const close = () => setCoords(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [coords]);

  const show = () => {
    if (!enabled || suppressed.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setCoords({ top: rect.top + rect.height / 2, left: rect.right + GAP });
    }, SHOW_DELAY_MS);
  };

  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setCoords(null);
  };

  return (
    <div
      ref={anchorRef}
      className={className}
      onMouseEnter={show}
      onMouseLeave={() => {
        suppressed.current = false;
        hide();
      }}
      onPointerDown={() => {
        suppressed.current = true;
        hide();
      }}
      // Solo el foco de teclado: el que devuelve un panel al cerrarse no lo abre.
      onFocus={(event) => {
        if (isKeyboardFocus(event.target)) show();
      }}
      onBlur={hide}
    >
      {children}
      {enabled &&
        coords &&
        typeof document !== 'undefined' &&
        createPortal(
          // Mismo globo que el de las (i) (`components/Tooltip`): texto blanco al 92%,
          // 9/12 de relleno, interlineado 1.5 y la sombra larga y suave.
          <span
            aria-hidden
            style={{ position: 'fixed', top: coords.top, left: coords.left }}
            className="pointer-events-none z-max -translate-y-1/2 whitespace-nowrap rounded-lg bg-frost-ink px-3 py-[9px] text-fs-12-5 font-medium leading-normal text-frost-card/92 shadow-[0_12px_28px_-12px_rgba(var(--f-ink-rgb),0.55)] motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
          >
            {label}
            <span
              aria-hidden
              className="absolute top-1/2 -left-1 h-2 w-2 -translate-y-1/2 rotate-45 rounded-[1px] bg-frost-ink"
            />
          </span>,
          document.body
        )}
    </div>
  );
};
